/**
 * Browser and device detection utilities
 */

export interface BrowserInfo {
  isSafari: boolean;
  isIOS: boolean;
  isMacOS: boolean;
  isMobile: boolean;
  isDesktop: boolean;
  isInstagramBrowser: boolean;
  isFacebookBrowser: boolean;
  isInAppBrowser: boolean;
  userAgent: string;
}

/**
 * Detect browser and device information
 */
export function detectBrowser(): BrowserInfo {
  const userAgent = navigator.userAgent;
  
  // Safari detection - must not be Chrome or other WebKit browsers
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
  
  // iOS detection
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  
  // macOS detection
  const isMacOS = /Macintosh|MacIntel|MacPPC|Mac68K/.test(userAgent);
  
  // Mobile detection (iOS + Android)
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  
  // Desktop detection
  const isDesktop = !isMobile;
  
  // In-app browser detection
  // Instagram browser detection - checks for Instagram or FBAN/FBAV (Facebook App)
  const isInstagramBrowser = /Instagram/i.test(userAgent) || 
    (/FBAN|FBAV/i.test(userAgent) && /Instagram/i.test(userAgent));
  
  // Facebook browser detection
  const isFacebookBrowser = /FBAN|FBAV/i.test(userAgent) && !isInstagramBrowser;
  
  // Any in-app browser (Instagram, Facebook, LinkedIn, Twitter, etc.)
  const isInAppBrowser = isInstagramBrowser || 
    isFacebookBrowser || 
    /LinkedIn/i.test(userAgent) || 
    /Twitter/i.test(userAgent) ||
    /Line/i.test(userAgent) ||
    /WeChat/i.test(userAgent) ||
    /Snapchat/i.test(userAgent) ||
    /TikTok/i.test(userAgent);
  
  return {
    isSafari,
    isIOS,
    isMacOS,
    isMobile,
    isDesktop,
    isInstagramBrowser,
    isFacebookBrowser,
    isInAppBrowser,
    userAgent
  };
}

/**
 * Check if the current environment is Safari on iOS or macOS
 */
export function isSafariApple(): boolean {
  const { isSafari, isIOS, isMacOS } = detectBrowser();
  return isSafari && (isIOS || isMacOS);
}

/**
 * Attempt to open Safari settings (iOS/macOS)
 * Note: Deep links to settings are restricted in recent iOS versions
 */
export function openSafariSettings(): Promise<boolean> {
  return new Promise((resolve) => {
    const { isIOS, isMacOS } = detectBrowser();
    
    if (isIOS) {
      // iOS no longer allows deep links to settings since iOS 10+
      // All App-Prefs: and prefs: URL schemes are blocked for security
      // The only solution is to show manual instructions
      console.log('iOS Safari detected - automatic settings opening not possible, showing manual instructions');
      resolve(false);
    } else if (isMacOS) {
      // For macOS Safari, we can't directly open Safari preferences due to security restrictions
      // The best we can do is provide clear, actionable instructions
      console.log('macOS Safari detected - providing manual instructions');
      resolve(false);
    } else {
      resolve(false);
    }
  });
}

/**
 * Get user-friendly instructions for enabling popups
 */
export function getPopupInstructions(): { title: string; steps: string[] } {
  const { isIOS, isMacOS } = detectBrowser();
  
  if (isIOS) {
    return {
      title: 'Enable Popups in iOS Settings',
      steps: [
        'Open the Settings app on your device',
        'Scroll down and tap "Safari"',
        'Under "General", find "Block Pop-ups"',
        'Turn OFF "Block Pop-ups" (toggle should be gray)',
        'Return to MockTrail and refresh the page to try again'
      ]
    };
  } else if (isMacOS) {
    return {
      title: 'Enable Popups in Safari',
      steps: [
        'In Safari, go to the menu bar and click "Safari" → "Preferences"',
        'Click the "Websites" tab at the top',
        'In the left sidebar, scroll down and click "Pop-up Windows"',
        'Find "platform.mocktrail.app" in the website list (or click "Currently Open Websites")',
        'Change the setting from "Block" to "Allow"',
        'Close the preferences window and refresh this page'
      ]
    };
  }
  
  // Generic fallback
  return {
    title: 'Enable Popups in Browser',
    steps: [
      'Look for a popup blocker icon in your browser\'s address bar',
      'Click on it and select "Always allow popups"',
      'Refresh the page to try again'
    ]
  };
}

/**
 * Check if the browser is Instagram's in-app browser
 */
export function isInstagramBrowser(): boolean {
  return detectBrowser().isInstagramBrowser;
}

/**
 * Check if the browser is any social media in-app browser
 */
export function isInAppBrowser(): boolean {
  return detectBrowser().isInAppBrowser;
} 